import React, { useRef, useState, useMemo } from 'react'
import styled from 'styled-components/macro'
import { useSelector } from 'react-redux'
import { message } from 'antd'
import { API } from 'src/utils/interceptor'
import InviteIcon from 'src/components/icons/InviteIcon'
import TrackIcon from 'src/components/icons/TrackIcon'
import EarnIcon from 'src/components/icons/EarnIcon'

const PLAN_PRICES = {
  starter: 29,
  growth: 79,
  pro: 149,
}

const IncentiveScheme = () => {
  const formRef = useRef(null)
  const account = useSelector(state => state.auth.account)
  const [venueCount, setVenueCount] = useState('')
  const [selectedPlan, setSelectedPlan] = useState('')

  // Form state
  const [fullName, setFullName] = useState('')
  const [email, setEmail] = useState('')
  const [companyName, setCompanyName] = useState(
    account?.supplier[0]?.title || '',
  )
  const [phoneNumber, setPhoneNumber] = useState('')
  const [note, setNote] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errors, setErrors] = useState({})

  const handleScrollToForm = () => {
    if (formRef.current) {
      formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  const commission = useMemo(() => {
    const venues = Number(venueCount) || 0
    const planPrice =
      selectedPlan && PLAN_PRICES[selectedPlan] ? PLAN_PRICES[selectedPlan] : 0
    const total = venues * planPrice
    return total >= 0 ? total : 0
  }, [venueCount, selectedPlan])

  const formattedCommission = `£${commission.toLocaleString('en-GB')}`

  const handleVenueCountChange = e => {
    const value = e.target.value
    setVenueCount(value)
  }

  const handlePlanChange = e => {
    setSelectedPlan(e.target.value)
  }

  const validateEmail = email => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return re.test(email)
  }

  const validateForm = () => {
    const newErrors = {}

    if (!fullName.trim()) {
      newErrors.fullName = 'Full Name is required'
    }

    if (!email.trim()) {
      newErrors.email = 'Email is required'
    } else if (!validateEmail(email)) {
      newErrors.email = 'Invalid email format'
    }

    if (!companyName.trim()) {
      newErrors.companyName = 'Company / Supplier Name is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async e => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    setIsSubmitting(true)

    try {
      await API.post('api2', 'suppliers/incentive-scheme/register', {
        body: {
          fullName: fullName.trim(),
          email: email.trim(),
          companyName: companyName.trim(),
          phoneNumber: phoneNumber.trim() || undefined,
          note: note.trim() || undefined,
          supplierId: account?.supplier[0]?._id,
        },
      })

      message.success("Thanks — we'll contact you within 2 business days.")

      // Reset form
      setFullName('')
      setEmail('')
      setCompanyName(account?.supplier[0]?.title || '')
      setPhoneNumber('')
      setNote('')
      setErrors({})
    } catch (error) {
      console.error('Form submission error:', error)
      message.error('Failed to submit. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <PageWrapper>
      <HeroCard>
        <HeroHeadline>
          Earn Commission by Inviting the Customers You Already Serve
        </HeroHeadline>
        <HeroSubheadline>
          No integration needed. Invite customers instantly. Ordering stays free
          forever.
        </HeroSubheadline>
        <GreedTrigger>
          One chain = hundreds in commission. Easy money for making your
          customers&apos; lives easier.
        </GreedTrigger>
        <TrustLine>
          Hundreds of suppliers already use OMS to receive orders.
        </TrustLine>
        <PrimaryCTA type='button' onClick={handleScrollToForm}>
          Register interest
        </PrimaryCTA>
      </HeroCard>

      <SectionCard>
        <SectionTitle>Why suppliers love OMS</SectionTitle>
        <CardGrid>
          <ValueCard>
            <ValueTitle>More accurate orders</ValueTitle>
            <ValueText>
              Fewer missed SKUs. Fewer phone calls. Everything logged in one
              place.
            </ValueText>
          </ValueCard>
          <ValueCard>
            <ValueTitle>Suppliers see bigger order sizes</ValueTitle>
            <ValueText>
              Digital ordering increases basket value — it&apos;s proven.
            </ValueText>
          </ValueCard>
          <ValueCard>
            <ValueTitle>Save hours every week</ValueTitle>
            <ValueText>
              OMS cuts down manual admin, missed voicemails, and WhatsApp chaos.
            </ValueText>
          </ValueCard>
          <ValueCard>
            <ValueTitle>Keep your customers loyal</ValueTitle>
            <ValueText>
              Digital ordering makes switching suppliers harder. OMS helps you
              retain accounts.
            </ValueText>
          </ValueCard>
          <ValueCard>
            <ValueTitle>Free forever for ordering</ValueTitle>
            <ValueText>
              Your customers can order from you at zero cost — no barriers.
            </ValueText>
          </ValueCard>
        </CardGrid>
      </SectionCard>

      <SectionCard>
        <SectionTitle>How it Works</SectionTitle>
        <StepsGrid>
          <StepCard>
            <StepIconWrapper>
              <InviteIcon size='32px' />
            </StepIconWrapper>
            <StepTitle>Step 1 — Invite your customers</StepTitle>
            <StepText>
              No tech setup needed. You can invite customers instantly using
              your normal Customer list.
            </StepText>
          </StepCard>
          <StepCard>
            <StepIconWrapper>
              <TrackIcon size='32px' />
            </StepIconWrapper>
            <StepTitle>Step 2 — OMS tracks everything automatically</StepTitle>
            <StepText>
              Every venue invited through your account is tied to you
              permanently. If they upgrade later, your commission is guaranteed.
            </StepText>
          </StepCard>
          <StepCard>
            <StepIconWrapper>
              <EarnIcon size='32px' />
            </StepIconWrapper>
            <StepTitle>Step 3 — Get paid when they upgrade</StepTitle>
            <StepText>
              If a venue chooses a paid module (Menu Costing, Recipes,
              Integrations, etc.), you earn 100% of their first month&apos;s
              subscription.
            </StepText>
            <StepExample>
              Example: 3 venues on OMS Pro = £447 in commission.
            </StepExample>
          </StepCard>
        </StepsGrid>
      </SectionCard>

      <SectionCard>
        <SectionTitle>Commission Calculator</SectionTitle>
        <CalculatorCard>
          <CalculatorInputs>
            <InputGroup>
              <InputLabel>Number of venues</InputLabel>
              <NumberInput
                type='number'
                min='0'
                placeholder='0'
                value={venueCount}
                onChange={handleVenueCountChange}
              />
            </InputGroup>
            <InputGroup>
              <InputLabel>Plan</InputLabel>
              <PlanSelect value={selectedPlan} onChange={handlePlanChange}>
                <option value=''>Select plan</option>
                <option value='starter'>Starter (£29)</option>
                <option value='growth'>Growth (£79)</option>
                <option value='pro'>Pro (£149)</option>
              </PlanSelect>
            </InputGroup>
          </CalculatorInputs>
          <CalculatorResult>
            Your estimated commission: {formattedCommission}
          </CalculatorResult>
          <CalculatorExample>
            Example: 3 Pro venues = £447 commission.
          </CalculatorExample>
        </CalculatorCard>
      </SectionCard>

      <SectionCard>
        <SectionTitle>Why OMS offers commissions</SectionTitle>
        <TransparencyText>
          OMS is free for ordering because suppliers like you invite your
          customers. We only charge for optional advanced features. When your
          venues upgrade, that revenue keeps OMS free — and we share the first
          month with you as a thank-you.
        </TransparencyText>
      </SectionCard>

      <SectionCard>
        <IntegrationNote>
          <IntegrationTitle>
            Want automation later? We&apos;ll integrate your system for free.
          </IntegrationTitle>
          <IntegrationText>
            Orders, invoices, and price files can flow automatically — if you
            ever want them to. Not required for the incentive programme. Just
            mention it when we call you.
          </IntegrationText>
        </IntegrationNote>
      </SectionCard>

      <SectionCard ref={formRef}>
        <SectionTitle>Register Interest</SectionTitle>
        <FormContainer onSubmit={handleSubmit}>
          <FormField>
            <InputLabel htmlFor='incentive-full-name'>Full Name</InputLabel>
            <TextInput
              id='incentive-full-name'
              type='text'
              placeholder='Your full name'
              value={fullName}
              onChange={e => setFullName(e.target.value)}
            />
            {errors.fullName && <ErrorMessage>{errors.fullName}</ErrorMessage>}
          </FormField>
          <FormField>
            <InputLabel htmlFor='incentive-email'>Email</InputLabel>
            <TextInput
              id='incentive-email'
              type='email'
              placeholder='your.email@example.com'
              value={email}
              onChange={e => setEmail(e.target.value)}
            />
            {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
          </FormField>
          <FormField>
            <InputLabel htmlFor='incentive-company-name'>
              Company / Supplier Name
            </InputLabel>
            <TextInput
              id='incentive-company-name'
              type='text'
              placeholder='Your company name'
              value={companyName}
              onChange={e => setCompanyName(e.target.value)}
            />
            {errors.companyName && (
              <ErrorMessage>{errors.companyName}</ErrorMessage>
            )}
          </FormField>
          <FormField>
            <InputLabel htmlFor='incentive-phone'>
              Phone Number (optional)
            </InputLabel>
            <TextInput
              id='incentive-phone'
              type='tel'
              placeholder='Your phone number'
              value={phoneNumber}
              onChange={e => setPhoneNumber(e.target.value)}
            />
          </FormField>
          <FormField>
            <InputLabel htmlFor='incentive-note'>Optional note</InputLabel>
            <TextAreaInput
              id='incentive-note'
              rows={4}
              placeholder='Any additional information...'
              value={note}
              onChange={e => setNote(e.target.value)}
            />
          </FormField>
          <FormSubmitButton type='submit' disabled={isSubmitting}>
            {isSubmitting ? 'Submitting...' : 'Register interest'}
          </FormSubmitButton>
        </FormContainer>
      </SectionCard>
    </PageWrapper>
  )
}

const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--sp-space-xxl);

  @media (max-width: 1024px) {
    gap: var(--sp-space-xl);
  }

  @media (max-width: 768px) {
    gap: var(--sp-space-lg);
  }
`

const SectionCard = styled.section`
  background: var(--sp-color-surface);
  border-radius: var(--sp-radius-card);
  padding: var(--sp-space-xl);
  box-shadow: var(--sp-shadow-card);
  border: 1px solid var(--sp-color-border);

  @media (max-width: 768px) {
    padding: var(--sp-space-md);
  }
`

const HeroCard = styled(SectionCard)`
  text-align: left;
`

const HeroHeadline = styled.h1`
  margin: 0 0 var(--sp-space-md);
  color: var(--sp-color-text);
  font-size: 32px;
  font-weight: 700;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 24px;
  }
`

const HeroSubheadline = styled.p`
  margin: 0 0 var(--sp-space-md);
  color: var(--sp-color-text);
  font-size: 18px;
  line-height: 1.5;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`

const GreedTrigger = styled.p`
  margin: var(--sp-space-lg) 0 var(--sp-space-sm);
  color: var(--sp-color-text);
  font-size: 18px;
  font-weight: 700;
  line-height: 1.5;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`

const TrustLine = styled.p`
  margin: 0 0 var(--sp-space-lg);
  color: var(--sp-color-muted);
  font-size: 15px;
  line-height: 1.5;
`

const PrimaryCTA = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 var(--sp-space-xl);
  min-height: 48px;
  border-radius: var(--sp-radius-control);
  border: none;
  background: var(--sp-color-primary);
  color: #fff;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.9;
  }

  &:focus {
    outline: 2px solid var(--sp-color-primary);
    outline-offset: 2px;
  }
`

const SectionTitle = styled.h2`
  margin: 0 0 var(--sp-space-lg);
  color: var(--sp-color-text);
  font-size: 24px;
  font-weight: 700;

  @media (max-width: 768px) {
    font-size: 20px;
  }
`

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: var(--sp-space-md);
  margin-top: var(--sp-space-md);

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`

const ValueCard = styled.div`
  padding: var(--sp-space-md);
  border-radius: var(--sp-radius-card);
  background: rgba(14, 165, 233, 0.04);
  border: 1px solid var(--sp-color-border);
`

const ValueTitle = styled.h3`
  margin: 0 0 var(--sp-space-sm);
  color: var(--sp-color-text);
  font-size: 18px;
  font-weight: 700;
`

const ValueText = styled.p`
  margin: 0;
  color: var(--sp-color-muted);
  font-size: 15px;
  line-height: 1.6;
`

const StepsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: var(--sp-space-lg);
  margin-top: var(--sp-space-md);

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: var(--sp-space-md);
  }
`

const StepCard = styled.div`
  padding: var(--sp-space-lg);
  border-radius: var(--sp-radius-card);
  background: rgba(14, 165, 233, 0.04);
  border: 1px solid var(--sp-color-border);
`

const StepIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: var(--sp-color-primary);
  color: #fff;
  margin-bottom: var(--sp-space-md);

  svg,
  svg path {
    fill: #fff;
    color: #fff;
  }
`

const StepTitle = styled.h3`
  margin: 0 0 var(--sp-space-sm);
  color: var(--sp-color-text);
  font-size: 18px;
  font-weight: 700;
`

const StepText = styled.p`
  margin: 0 0 var(--sp-space-sm);
  color: var(--sp-color-muted);
  font-size: 15px;
  line-height: 1.6;
`

const StepExample = styled.p`
  margin: var(--sp-space-sm) 0 0;
  color: var(--sp-color-text);
  font-size: 15px;
  font-weight: 600;
  font-style: italic;
`

const CalculatorCard = styled.div`
  padding: var(--sp-space-lg);
  border-radius: var(--sp-radius-card);
  background: rgba(14, 165, 233, 0.04);
  border: 1px solid var(--sp-color-border);
  margin-top: var(--sp-space-md);
`

const CalculatorInputs = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: var(--sp-space-md);
  margin-bottom: var(--sp-space-lg);

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--sp-space-xs);
`

const InputLabel = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: var(--sp-color-text);
`

const NumberInput = styled.input`
  padding: var(--sp-space-sm) var(--sp-space-md);
  border: 1px solid var(--sp-color-border);
  border-radius: var(--sp-radius-control);
  font-size: 15px;
  background: var(--sp-color-surface);
  color: var(--sp-color-text);
  transition: border-color 0.2s ease;

  &:hover:not(:disabled) {
    border-color: var(--sp-color-primary);
  }

  &:focus {
    outline: 2px solid var(--sp-color-primary);
    outline-offset: 2px;
    border-color: var(--sp-color-primary);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

const PlanSelect = styled.select`
  padding: var(--sp-space-sm) var(--sp-space-md);
  border: 1px solid var(--sp-color-border);
  border-radius: var(--sp-radius-control);
  font-size: 15px;
  background: var(--sp-color-surface);
  color: var(--sp-color-text);
  transition: border-color 0.2s ease;

  &:hover:not(:disabled) {
    border-color: var(--sp-color-primary);
  }

  &:focus {
    outline: 2px solid var(--sp-color-primary);
    outline-offset: 2px;
    border-color: var(--sp-color-primary);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

const CalculatorResult = styled.div`
  margin: var(--sp-space-lg) 0 var(--sp-space-sm);
  padding: var(--sp-space-md);
  background: var(--sp-color-surface);
  border-radius: var(--sp-radius-control);
  font-size: 20px;
  font-weight: 700;
  color: var(--sp-color-text);
  text-align: center;
`

const CalculatorExample = styled.p`
  margin: 0;
  color: var(--sp-color-muted);
  font-size: 14px;
  text-align: center;
  font-style: italic;
`

const TransparencyText = styled.p`
  margin: var(--sp-space-md) 0 0;
  color: var(--sp-color-text);
  font-size: 16px;
  line-height: 1.7;
`

const IntegrationNote = styled.div`
  padding: var(--sp-space-lg);
  border-radius: var(--sp-radius-card);
  background: rgba(14, 165, 233, 0.04);
  border: 1px solid var(--sp-color-border);
`

const IntegrationTitle = styled.h3`
  margin: 0 0 var(--sp-space-sm);
  color: var(--sp-color-text);
  font-size: 18px;
  font-weight: 700;
`

const IntegrationText = styled.p`
  margin: 0;
  color: var(--sp-color-muted);
  font-size: 15px;
  line-height: 1.6;
`

const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  gap: var(--sp-space-md);
  margin-top: var(--sp-space-md);
  max-width: 600px;
  width: 100%;

  @media (max-width: 768px) {
    max-width: 100%;
  }
`

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--sp-space-xs);
`

const ErrorMessage = styled.span`
  color: var(--sp-color-danger);
  font-size: 13px;
  margin-top: 2px;
`

const TextInput = styled.input`
  padding: var(--sp-space-sm) var(--sp-space-md);
  border: 1px solid var(--sp-color-border);
  border-radius: var(--sp-radius-control);
  font-size: 15px;
  background: var(--sp-color-surface);
  color: var(--sp-color-text);
  transition: border-color 0.2s ease;

  &:hover:not(:disabled) {
    border-color: var(--sp-color-primary);
  }

  &:focus {
    outline: 2px solid var(--sp-color-primary);
    outline-offset: 2px;
    border-color: var(--sp-color-primary);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

const TextAreaInput = styled.textarea`
  padding: var(--sp-space-sm) var(--sp-space-md);
  border: 1px solid var(--sp-color-border);
  border-radius: var(--sp-radius-control);
  font-size: 15px;
  background: var(--sp-color-surface);
  color: var(--sp-color-text);
  font-family: inherit;
  resize: vertical;
  transition: border-color 0.2s ease;

  &:hover:not(:disabled) {
    border-color: var(--sp-color-primary);
  }

  &:focus {
    outline: 2px solid var(--sp-color-primary);
    outline-offset: 2px;
    border-color: var(--sp-color-primary);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

const FormSubmitButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 var(--sp-space-xl);
  min-height: 48px;
  border-radius: var(--sp-radius-control);
  border: none;
  background: var(--sp-color-primary);
  color: #fff;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s ease;
  margin-top: var(--sp-space-sm);

  &:hover:not(:disabled) {
    opacity: 0.9;
  }

  &:focus {
    outline: 2px solid var(--sp-color-primary);
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

export default IncentiveScheme
